import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Cpu, Gauge, Timer, CheckCircle2, AlertOctagon, ShieldX, Download, FileJson } from "lucide-react";
import { useEvent, useClassify, useAcknowledge, useCaseExport } from "../../lib/queries";
import { SeverityCapsule, Pill, Button, Spinner } from "../../components/ui";
import { MediaPanel } from "../../components/MediaIndicator";
import { EMESRT_COLOR, fmtTime } from "../../lib/format";
import type { Severity } from "../../types/api";
import DetectionOverlay from "./DetectionOverlay";
import RiskMatrix from "./RiskMatrix";
import MiniMap from "../map/MiniMap";

export default function ExceptionDetail({
  eventId,
  onClose,
}: {
  eventId: number | null;
  onClose: () => void;
}) {
  const { data: ev, isLoading } = useEvent(eventId);
  const classify = useClassify();
  const ack = useAcknowledge();
  const caseExport = useCaseExport();

  const [risk, setRisk] = useState<{ consequence: string; likelihood: string; severity_class: Severity } | null>(null);
  const [verdict, setVerdict] = useState<"true_positive" | "false_positive" | null>(null);
  const [rootCause, setRootCause] = useState("");
  const [corrective, setCorrective] = useState("");

  useEffect(() => {
    if (ev?.classification) {
      setVerdict(ev.classification.verdict === "false_positive" ? "false_positive" : "true_positive");
      setRootCause(ev.classification.root_cause);
      setCorrective(ev.classification.corrective_action);
      if (ev.classification.consequence) {
        setRisk({
          consequence: ev.classification.consequence,
          likelihood: ev.classification.likelihood,
          severity_class: (ev.classification.severity_class as Severity) || "medium",
        });
      }
    } else {
      setVerdict(null);
      setRisk(null);
      setRootCause("");
      setCorrective("");
    }
  }, [ev?.id]);

  function submit() {
    if (!ev || !verdict) return;
    classify.mutate({
      id: ev.id,
      body: {
        verdict,
        consequence: risk?.consequence ?? "",
        likelihood: risk?.likelihood ?? "",
        severity_class: risk?.severity_class ?? "",
        root_cause: rootCause,
        corrective_action: corrective,
      },
    });
  }

  const intervention = ev?.interventions?.[0];

  return (
    <AnimatePresence>
      {eventId != null && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="glass fixed right-0 top-0 z-50 h-screen w-full max-w-[560px] overflow-y-auto !rounded-none border-l border-white/10"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            {isLoading || !ev ? (
              <div className="p-8">
                <Spinner label="Loading exception…" />
              </div>
            ) : (
              <div className="p-5">
                {/* Header */}
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <div className="section-tag">EXCEPTION · #{ev.ext_id}</div>
                    <h2 className="mt-1 text-lg font-semibold">{ev.event_type_label}</h2>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <SeverityCapsule severity={ev.severity} />
                      <Pill color={EMESRT_COLOR[ev.emesrt_level]}>{ev.emesrt_level}</Pill>
                      {intervention && <Pill color="#F97316">{intervention.kind.replace(/_/g, " ")}</Pill>}
                      <span className="mono text-[10px] text-slatey">RISK {Math.round(ev.risk_score)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => caseExport.mutate({ id: ev.id, fmt: "pdf" })}
                      title="Export case as PDF"
                      className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-slatey transition hover:border-aurora-cyan/40 hover:text-ink"
                    >
                      <Download size={13} /> PDF
                    </button>
                    <button
                      onClick={() => caseExport.mutate({ id: ev.id, fmt: "json" })}
                      title="Export case as JSON"
                      className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-slatey transition hover:border-aurora-cyan/40 hover:text-ink"
                    >
                      <FileJson size={13} /> JSON
                    </button>
                    <button onClick={onClose} className="ml-1 text-slatey hover:text-ink">
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                  <Meta label="ASSET" value={ev.asset_registration ?? "—"} />
                  <Meta label="SITE" value={ev.site_title ?? "—"} />
                  <Meta label="CHANNEL" value={`CAM ${ev.channel ?? "—"}`} />
                  <Meta label="TIME" value={fmtTime(ev.start_time)} />
                  <Meta label="SPEED" value={ev.speed != null ? `${ev.speed.toFixed(1)} km/h` : "—"} />
                  <Meta label="DEAD-MAN" value={ev.dead_man ? "ACTIVE" : "—"} danger={ev.dead_man} />
                </div>

                {caseExport.isSuccess && (
                  <div className="mb-4 flex items-center gap-1.5 rounded-lg border border-aurora-teal/40 bg-aurora-teal/10 px-3 py-1.5 text-xs text-aurora-teal">
                    <CheckCircle2 size={13} /> Case export queued · view in
                    <a href="/exports" className="underline">Export History</a>
                  </div>
                )}

                {/* Media availability + evidence */}
                <Section title="MEDIA AVAILABILITY · 5s PRE/POST">
                  <MediaPanel
                    media={ev.media}
                    redZone={ev.event_type?.red_zone_m ?? 2.5}
                    advisoryZone={ev.event_type?.advisory_zone_m ?? 10}
                    modelVersion={ev.xai.model_version}
                    risk={ev.risk_score}
                    overlay={
                      <DetectionOverlay
                        risk={ev.risk_score}
                        redZone={ev.event_type?.red_zone_m ?? 2.5}
                        advisoryZone={ev.event_type?.advisory_zone_m ?? 10}
                        model={ev.xai.model_version}
                      />
                    }
                  />
                </Section>

                {/* XAI */}
                <Section title="ALGORITHMIC REASONING · XAI">
                  <ul className="space-y-1.5">
                    {ev.xai.drivers.map((d, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-aurora-cyan" />
                        {d}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3">
                    <div className="mb-1 flex justify-between text-[10px] text-slatey">
                      <span>CONFIDENCE</span>
                      <span className="mono">{(ev.xai.confidence * 100).toFixed(1)}%</span>
                    </div>
                    <div className="relative h-2 rounded-full bg-white/5">
                      <div
                        className="absolute inset-y-0 rounded-full bg-aurora-cyan/30"
                        style={{
                          left: `${ev.xai.confidence_band[0] * 100}%`,
                          right: `${100 - ev.xai.confidence_band[1] * 100}%`,
                        }}
                      />
                      <div
                        className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-aurora-cyan shadow-glow"
                        style={{ left: `calc(${ev.xai.confidence * 100}% - 6px)` }}
                      />
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <Stat icon={<Cpu size={13} />} label="MODEL" value={ev.xai.model_version} />
                    <Stat icon={<Timer size={13} />} label="LATENCY" value={`${ev.xai.latency_ms}ms`} />
                    <Stat icon={<Gauge size={13} />} label="EMESRT" value={ev.xai.emesrt_level} />
                  </div>
                </Section>

                {/* Location mini-map */}
                {ev.latitude != null && ev.longitude != null && (
                  <Section title="LOCATION">
                    <MiniMap lat={ev.latitude} lng={ev.longitude} severity={ev.severity} />
                  </Section>
                )}

                {/* Risk matrix */}
                <Section title="RISK MATRIX · CONSEQUENCE × LIKELIHOOD">
                  <RiskMatrix value={risk} onSelect={setRisk} />
                  {risk && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="text-slatey">Severity class →</span>
                      <SeverityCapsule severity={risk.severity_class} />
                    </div>
                  )}
                </Section>

                {/* Notes */}
                <Section title="ROOT CAUSE & CORRECTIVE ACTION">
                  <textarea
                    value={rootCause}
                    onChange={(e) => setRootCause(e.target.value)}
                    placeholder="Root cause…"
                    rows={2}
                    className="mb-2 w-full rounded-lg border border-white/10 bg-white/5 p-2 text-sm outline-none focus:border-aurora-cyan/40"
                  />
                  <textarea
                    value={corrective}
                    onChange={(e) => setCorrective(e.target.value)}
                    placeholder="Corrective action / crew brief…"
                    rows={2}
                    className="w-full rounded-lg border border-white/10 bg-white/5 p-2 text-sm outline-none focus:border-aurora-cyan/40"
                  />
                </Section>

                {/* Classification */}
                <Section title="CLASSIFICATION · FEEDS ACTIVE-LEARNING">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setVerdict("true_positive")}
                      className={`flex items-center justify-center gap-2 rounded-lg border py-2 text-sm transition ${
                        verdict === "true_positive"
                          ? "border-accent bg-accent/15 text-accent"
                          : "border-white/10 bg-white/5 text-slatey hover:text-ink"
                      }`}
                    >
                      <AlertOctagon size={15} /> True positive
                    </button>
                    <button
                      onClick={() => setVerdict("false_positive")}
                      className={`flex items-center justify-center gap-2 rounded-lg border py-2 text-sm transition ${
                        verdict === "false_positive"
                          ? "border-aurora-cyan bg-aurora-cyan/15 text-aurora-cyan"
                          : "border-white/10 bg-white/5 text-slatey hover:text-ink"
                      }`}
                    >
                      <ShieldX size={15} /> False positive
                    </button>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      onClick={submit}
                      disabled={!verdict || classify.isPending}
                      className="flex-1"
                    >
                      {classify.isPending ? "Saving…" : "Classify & Act"}
                    </Button>
                    <Button variant="ghost" onClick={() => ack.mutate(ev.id)}>
                      Acknowledge
                    </Button>
                  </div>
                  {ev.status === "classified" && classify.isSuccess && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-aurora-teal">
                      <CheckCircle2 size={14} /> Classified · crew briefed · label queued for retraining
                    </div>
                  )}
                </Section>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="section-tag mb-2">{title}</div>
      {children}
    </div>
  );
}

function Meta({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/5 p-2">
      <div className="section-tag">{label}</div>
      <div className={`mt-0.5 truncate text-xs font-medium ${danger ? "text-critical" : ""}`}>{value}</div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/5 p-2">
      <div className="flex items-center gap-1 text-slatey">{icon}<span className="section-tag">{label}</span></div>
      <div className="mono mt-0.5 truncate text-xs">{value}</div>
    </div>
  );
}
